/**
 * TrustGrid Table Component
 * Pre-configured table with export, column customization, and filters
 * Replaces 24+ table definitions across components
 */

import React, { useState, useMemo } from 'react';
import { Table, Button, Dropdown, Space, Checkbox } from 'antd';
import {
  DownloadOutlined,
  SettingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { spacing } from '../../theme/designTokens';
import EmptyState from '../layout/EmptyState';

/**
 * Export data to CSV
 */
const exportToCSV = (data, columns, filename = 'export.csv') => {
  if (!data || data.length === 0) return;

  // Get column headers
  const headers = columns
    .filter((col) => !col.hideInExport && col.dataIndex)
    .map((col) => col.title);

  // Get column data keys
  const keys = columns
    .filter((col) => !col.hideInExport && col.dataIndex)
    .map((col) => col.dataIndex);

  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      keys
        .map((key) => {
          const value = row[key];
          // Handle nested objects, arrays, etc.
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(',')
    ),
  ].join('\n');

  // Download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * TrustGridTable Component
 *
 * @param {array} columns - Table columns definition
 * @param {array} dataSource - Table data
 * @param {boolean} loading - Loading state
 * @param {object} tableProps - Props from useTable hook (pagination, onChange, etc.)
 * @param {boolean} showExport - Show export button
 * @param {string} exportFilename - Export filename
 * @param {boolean} showColumnCustomization - Show column visibility toggle
 * @param {boolean} showRefresh - Show refresh button
 * @param {function} onRefresh - Refresh handler
 * @param {string} size - Table size: 'small' | 'middle' | 'large'
 * @param {boolean} bordered - Show borders
 * @param {ReactNode} emptyText - Custom empty state
 * @param {string} emptyTitle - Empty state title
 * @param {string} emptyDescription - Empty state description
 * @param {object} style - Additional styles
 */
function TrustGridTable({
  columns = [],
  dataSource = [],
  loading = false,
  tableProps = {},
  showExport = false,
  exportFilename = 'export.csv',
  showColumnCustomization = false,
  showRefresh = false,
  onRefresh,
  size = 'middle',
  bordered = false,
  emptyText,
  emptyTitle = 'No Data',
  emptyDescription,
  style = {},
  ...restProps
}) {
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.reduce((acc, col) => {
      if (col.key || col.dataIndex) {
        acc[col.key || col.dataIndex] = true;
      }
      return acc;
    }, {})
  );

  // Filter columns based on visibility
  const displayColumns = useMemo(() => {
    return columns.filter((col) => {
      const key = col.key || col.dataIndex;
      return !key || visibleColumns[key] !== false;
    });
  }, [columns, visibleColumns]);

  // Handle export
  const handleExport = () => {
    exportToCSV(dataSource, displayColumns, exportFilename);
  };

  // Column customization menu
  const columnCustomizationMenu = {
    items: columns
      .filter((col) => col.key || col.dataIndex)
      .map((col) => {
        const key = col.key || col.dataIndex;
        return {
          key,
          label: (
            <Checkbox
              checked={visibleColumns[key] !== false}
              onChange={(e) => {
                setVisibleColumns((prev) => ({
                  ...prev,
                  [key]: e.target.checked,
                }));
              }}
            >
              {col.title}
            </Checkbox>
          ),
        };
      }),
  };

  // Actions toolbar
  const showToolbar = showExport || showColumnCustomization || showRefresh;

  const toolbar = showToolbar && (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: spacing.md,
        gap: spacing.xs,
      }}
    >
      <Space size="small">
        {showRefresh && onRefresh && (
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            Refresh
          </Button>
        )}
        {showExport && (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!dataSource || dataSource.length === 0}
          >
            Export
          </Button>
        )}
        {showColumnCustomization && (
          <Dropdown menu={columnCustomizationMenu} trigger={['click']}>
            <Button icon={<SettingOutlined />}>Columns</Button>
          </Dropdown>
        )}
      </Space>
    </div>
  );

  // Custom empty state
  const locale = {
    emptyText: emptyText || (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        style={{ padding: `${spacing.xl}px 0` }}
      />
    ),
  };

  return (
    <div style={style}>
      {toolbar}
      <Table
        columns={displayColumns}
        dataSource={dataSource}
        loading={loading}
        size={size}
        bordered={bordered}
        locale={locale}
        {...tableProps}
        {...restProps}
      />
    </div>
  );
}

export default TrustGridTable;
